// Descuento de accesorios que se aparta solo en la ancla (sin `lineIds`, cubre todas las piezas)
// mientras haya un accesorio que califique. Puro, sin React.
import type { ReservedLine } from '@/domains/sales/wizard/types';
import type {
    EvaluatedSalePromotion,
    EvaluateSalePromotionsResponse,
    StoreSalePromotionItem,
} from '../api/wizardPromotionsContract';
import { targetsExtra } from './extrasBenefits';
import { onlyLowersAccessories } from './phoneDraft';
import { forAnchor } from './promotionAppliesTo';
import { choicesOn, type PromotionSelection } from './promotionSelection';

/** Accesorios de la venta que la promo baja: por su alcance o por los renglones evaluados. */
export function qualifyingExtras(
    promo: EvaluatedSalePromotion,
    extraLines: readonly ReservedLine[],
    store: readonly StoreSalePromotionItem[],
): ReservedLine[] {
    const scoped = store.find((p) => p.promotionId === promo.promotionId);
    return extraLines.filter((line) =>
        scoped
            ? targetsExtra(
                  {
                      sku: line.sku,
                      category: line.product?.category,
                      tier: line.product?.tier ?? '',
                  },
                  scoped,
              )
            : promo.lines.some((l) => l.lineId === line.lineId),
    );
}

/** Promos cuyo único efecto es bajar accesorios, por la evaluación o por la tienda. */
export function accessoryOnlyIds(
    evaluation: EvaluateSalePromotionsResponse | undefined,
    store: readonly StoreSalePromotionItem[],
): Set<string> {
    const ids = new Set(
        (evaluation?.promotions ?? []).filter(onlyLowersAccessories).map((p) => p.promotionId),
    );
    for (const p of store) {
        const { accessoryDiscount, ...rest } = p.levers;
        if (accessoryDiscount && Object.values(rest).every((v) => !v)) ids.add(p.promotionId);
    }
    return ids;
}

/** La primera (A-Z) que se puede apartar y baja algún accesorio de la venta. */
export function accessoryDiscountCandidate(
    evaluation: EvaluateSalePromotionsResponse,
    store: readonly StoreSalePromotionItem[],
    extraLines: readonly ReservedLine[],
    withoutSureKeep: boolean,
): EvaluatedSalePromotion | undefined {
    return forAnchor(
        evaluation.promotions.filter((p) => onlyLowersAccessories(p) && !p.blockedBy),
        withoutSureKeep,
    )
        .filter((p) => qualifyingExtras(p, extraLines, store).length > 0)
        .sort((a, b) => a.name.localeCompare(b.name, 'es-MX'))
        .at(0);
}

/** Las de solo accesorios apartadas en la ancla. */
export function heldAccessoryDiscounts(
    selection: PromotionSelection,
    anchorLineId: string,
    accessoryOnly: ReadonlySet<string>,
): string[] {
    return choicesOn(selection, anchorLineId)
        .map((c) => c.promotionId)
        .filter((id) => accessoryOnly.has(id));
}

/** La selección sin esas promos en la línea. */
export function withoutPromotionsOn(
    selection: PromotionSelection,
    lineId: string,
    ids: readonly string[],
): PromotionSelection {
    const line = selection.byLine[lineId];
    if (!line) return selection;
    const solo = line.solo && ids.includes(line.solo.promotionId) ? null : line.solo;
    const stackables = line.stackables.filter((c) => !ids.includes(c.promotionId));
    return { ...selection, byLine: { ...selection.byLine, [lineId]: { solo, stackables } } };
}
